import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Pagination,
  Menu,
  MenuItem,
  styled,
  alpha,
  useTheme,
  useMediaQuery,
  Tooltip,
  TextField,
  Button,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddnewSchool from "./AddnewSchool";

const ModernTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 500,
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: alpha(theme.palette.primary.main, 0.02),
  },
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
    transition: "background-color 0.2s ease",
  },
}));

const ResponsiveTableContainer = styled(TableContainer)(({ theme }) => ({
  maxHeight: "600px",
  overflowX: "auto",
}));

function Schools() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: "id", direction: "desc" });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [openAddNewSchool, setOpenAddNewSchool] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const itemsPerPage = 5;

  useEffect(() => {
    fetchSchools();
  }, []);

  const fetchSchools = async () => {
    try {
      setLoading(true);
      const res = await fetch("/api/support/schools");
      const data = await res.json();
      const list = data.recordset || data;
      setSchools(list);
    } catch (error) {
      console.error("Error fetching schools:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key) {
      direction = sortConfig.direction === "asc" ? "desc" : "asc";
    }
    setSortConfig({ key, direction });
  };

  const filteredSchools = schools.filter((item) => {
    const search = searchTerm.toLowerCase();
    return (
      item.School?.toLowerCase().includes(search) ||
      item.Country?.toLowerCase().includes(search)
    );
  });

  const sortedSchools = [...filteredSchools].sort((a, b) => {
    if (!sortConfig.key) return 0;
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];
    if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedSchools.slice(indexOfFirstItem, indexOfLastItem);

  const handleMenuOpen = (event, item) => {
    setAnchorEl(event.currentTarget);
    setSelectedSchool(item);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedSchool(null);
  };

  const handleAddNew = () => setOpenAddNewSchool(true);

  return (
    <Box sx={{ backgroundColor: "white", borderRadius: "16px", p: 3, mb: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Schools
        </Typography>

        <TextField
          size="small"
          placeholder="Search school or country"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          sx={{ minWidth: 280 }}
        />

        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddNew}
        >
          Add New School
        </Button>
      </Box>

      {/* Table */}
      <ResponsiveTableContainer component={Paper}>
        <Table size={isMobile ? "small" : "medium"}>
          <TableHead>
            <TableRow
              sx={{
                bgcolor: theme.palette.primary.light,
                "& th": {
                  color: "white",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                },
              }}
            >
              {[
                { id: "index", label: "ID" },
                { id: "School", label: "School" },
                { id: "Country", label: "Country" },
                { id: "actions", label: "Action" },
              ].map((column) => (
                <ModernTableCell
                  key={column.id}
                  onClick={() =>
                    column.id !== "actions" && handleSort(column.id)
                  }
                  sx={{
                    cursor: column.id !== "actions" ? "pointer" : "default",
                  }}
                >
                  {column.label}
                </ModernTableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <ModernTableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Loading schools...
                  </Typography>
                </ModernTableCell>
              </TableRow>
            ) : currentItems.length > 0 ? (
              currentItems.map((item, index) => (
                <StyledTableRow key={item.id} hover>
                  {/* Sequential ID */}
                  <ModernTableCell>
                    {indexOfFirstItem + index + 1}
                  </ModernTableCell>

                  <ModernTableCell>{item.School}</ModernTableCell>
                  <ModernTableCell>{item.Country}</ModernTableCell>
                  <ModernTableCell>
                    <Tooltip title="More actions" arrow>
                      <IconButton onClick={(e) => handleMenuOpen(e, item)}>
                        <MoreVertIcon fontSize={isMobile ? "small" : "medium"} />
                      </IconButton>
                    </Tooltip>
                  </ModernTableCell>
                </StyledTableRow>
              ))
            ) : (
              <TableRow>
                <ModernTableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1">No schools found</Typography>
                </ModernTableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </ResponsiveTableContainer>

      {/* Pagination */}
      <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
        <Pagination
          count={Math.ceil(filteredSchools.length / itemsPerPage)}
          page={currentPage}
          onChange={(e, value) => setCurrentPage(value)}
          color="primary"
          size={isMobile ? "small" : "medium"}
        />
      </Box>

      {/* Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleMenuClose}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <DeleteIcon fontSize="small" color="error" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Add New School Modal */}
      <AddnewSchool
        open={openAddNewSchool}
        onClose={() => setOpenAddNewSchool(false)}
        onSubmit={() => fetchSchools()}
      />
    </Box>
  );
}

export default Schools;
